import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, Phone, X } from "lucide-react";
import { Wordmark } from "./Logo";
import { SITE } from "../lib/site";

const LINKS = [
    { to: "/", label: "Home", id: "home" },
    { to: "/about", label: "About Us", id: "about" },
    { to: "/services", label: "Our Services", id: "services" },
    { to: "/trailer-availability", label: "Trailer Availability", id: "trailers" },
    { to: "/why-choose-us", label: "Why Choose Us", id: "why-us" },
    { to: "/contact", label: "Contact Us", id: "contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <>
            <header
                className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
                    scrolled
                        ? "border-b border-line bg-paper/85 backdrop-blur-md"
                        : "border-b border-transparent bg-transparent"
                }`}
            >
                <div className="container-x flex h-20 items-center justify-between gap-4">
                    <Link to="/" data-testid="nav-logo-link" aria-label="OSM Cargo Movers home">
                        <Wordmark />
                    </Link>

                    <nav className="hidden xl:flex items-center gap-7">
                        {LINKS.map((l) => (
                            <NavLink
                                key={l.id}
                                to={l.to}
                                data-testid={`nav-link-${l.id}`}
                                className={({ isActive }) =>
                                    `whitespace-nowrap text-[13px] font-semibold tracking-wide transition-colors hover:text-brand ${
                                        isActive ? "text-ink" : "text-steel"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <a
                            href={`tel:${SITE.phones[0].tel}`}
                            data-testid="nav-hotline-link"
                            className="hidden 2xl:inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-line bg-white px-4 py-2.5 text-[13px] font-semibold text-ink transition-all hover:border-ink"
                        >
                            <Phone className="h-3.5 w-3.5 text-brand" />
                            {SITE.phones[0].display}
                        </a>
                        <Link
                            to="/contact#quote"
                            data-testid="nav-get-quote-btn"
                            className="hidden lg:inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-ink px-5 py-2.5 text-[13px] font-semibold text-paper transition-all duration-300 hover:bg-brand hover:-translate-y-0.5"
                        >
                            Get a Trailer Quote
                            <ArrowUpRight className="h-4 w-4" />
                        </Link>
                        <button
                            onClick={() => setOpen((v) => !v)}
                            data-testid="nav-mobile-toggle"
                            aria-label={open ? "Close menu" : "Open menu"}
                            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-line bg-white text-ink xl:hidden"
                        >
                            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-40 bg-paper pt-24 xl:hidden"
                        data-testid="nav-mobile-drawer"
                    >
                        <nav className="container-x flex flex-col divide-y divide-line">
                            {LINKS.map((l, i) => (
                                <motion.div
                                    key={l.id}
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.05 * i, duration: 0.4 }}
                                >
                                    <NavLink
                                        to={l.to}
                                        data-testid={`nav-mobile-link-${l.id}`}
                                        className={({ isActive }) =>
                                            `flex items-center justify-between py-4 font-display text-2xl font-bold uppercase tracking-tight ${
                                                isActive ? "text-brand" : "text-ink"
                                            }`
                                        }
                                    >
                                        {l.label}
                                        <ArrowUpRight className="h-5 w-5 text-brand" />
                                    </NavLink>
                                </motion.div>
                            ))}
                        </nav>
                        <div className="container-x mt-8 flex flex-col gap-3">
                            <Link
                                to="/contact#quote"
                                data-testid="nav-mobile-get-quote-btn"
                                className="btn-primary w-full"
                            >
                                Get a Trailer Quote
                            </Link>
                            <a href={`tel:${SITE.phones[0].tel}`} className="btn-outline w-full">
                                <Phone className="h-4 w-4" /> Call {SITE.phones[0].display}
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
